/**
 * Page Metadata Injection System
 *
 * Lightweight alternative to Helmet for dynamic route metadata.
 * Allows route components to set page title and meta tags during render,
 * which are then extracted by entry-server.tsx during SSR.
 *
 * This system works alongside the centralized routeMetaMap:
 * - Static routes: metadata from routeMetaMap (no runtime setup needed)
 * - Dynamic routes: call setPageMetadata() with actual data from loaders
 *
 * @module utils/page-metadata
 */

/**
 * Global key for storing page metadata during SSR render
 */
const PAGE_METADATA_GLOBAL_KEY = '__VIRI_PAGE_METADATA__';

/**
 * Page metadata structure (subset of RouteMetaConfig without functions)
 */
export interface PageMetadata {
  title: string;
  description: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'profile' | 'product';
  twitterCard?: 'summary' | 'summary_large_image';
  jsonLd?: object | null; // JSON-LD structured data object (will be stringified)
}

/**
 * Ensures the global storage exists on the current execution context.
 * Works both in Node (SSR) and browser (client-side navigation).
 */
function ensureGlobalStorage(): Record<string, PageMetadata | null> {
  // Use globalThis to access both Node global and browser window
  const globalObj = typeof globalThis !== 'undefined' ? globalThis : {};
  if (!globalObj[PAGE_METADATA_GLOBAL_KEY]) {
    globalObj[PAGE_METADATA_GLOBAL_KEY] = null;
  }
  return globalObj;
}

/**
 * Set the page metadata during component render.
 *
 * Call this in your route component (not loader) to override the default
 * metadata from routeMetaMap with actual data from the loader.
 *
 * @example
 * ```tsx
 * function ItemPage() {
 *   const { initialTokenData } = Route.useLoaderData();
 *
 *   setPageMetadata({
 *     title: initialTokenData.title + ' | VIRI',
 *     description: initialTokenData.description,
 *     ogImage: initialTokenData.image,
 *     ogType: 'product',
 *   });
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function setPageMetadata(meta: PageMetadata): void {
  const storage = ensureGlobalStorage();
  storage[PAGE_METADATA_GLOBAL_KEY] = meta;
}

/**
 * Get the page metadata that was set during the most recent render.
 *
 * Called by entry-server.tsx after ReactDOMServer.renderToString()
 * to extract metadata that components set via setPageMetadata().
 *
 * @returns The page metadata, or null if none was set
 */
export function getPageMetadata(): PageMetadata | null {
  const storage = ensureGlobalStorage();
  return storage[PAGE_METADATA_GLOBAL_KEY];
}

/**
 * Clear the page metadata.
 *
 * Called by entry-server.tsx before each render to ensure clean state.
 */
export function clearPageMetadata(): void {
  const storage = ensureGlobalStorage();
  storage[PAGE_METADATA_GLOBAL_KEY] = null;
}

/**
 * Hook-like function to set multiple metadata properties at once.
 * Useful for routes with lots of dynamic data.
 *
 * @param overrides - Partial metadata to merge with defaults or previous values
 */
export function updatePageMetadata(overrides: Partial<PageMetadata>): void {
  const storage = ensureGlobalStorage();
  const current = storage[PAGE_METADATA_GLOBAL_KEY] || {};
  storage[PAGE_METADATA_GLOBAL_KEY] = {
    ...current,
    ...overrides,
  };
}